/**
 * Password input with show/hide toggle, wired into a react-hook-form `Control`.
 *
 * Shared between the web `/login` page and the native `MobileLoginScreen` so
 * the password field implementation lives in one place. Uses Untitled UI
 * icons per the project's design-system rule for new code.
 */

import { Eye, EyeOff } from '@untitled-ui/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AUTH_LABEL_CLASS, AUTH_PASSWORD_INPUT_CLASS } from '@/components/auth/auth-styles';
import { AuthTextField } from '@/components/auth/auth-text-field';
import { FormField } from '@/components/base/form/hook-form';
import { Button } from '@/components/base/buttons/button';

import type { Control, FieldValues, Path } from 'react-hook-form';

/**
 * Chrome for the reveal toggle, which sits on top of the input on every surface
 * the field renders on.
 *
 * Three of these classes are load-bearing and MUST NOT be dropped as redundant:
 *
 * - `hover:text-current` restates the resting colour because the shared `ghost`
 *   variant carries `hover:text-white`. Without it the icon turns white on
 *   hover and vanishes into the auth card and the entry paper alike; the base
 *   `text-current` override does not reach the `hover:` variant.
 * - `focus-visible:ring-current` rather than a theme-aware ring token: this
 *   field also renders on the entry canvas, which is cream in BOTH themes, so a
 *   token that flips with the theme is wrong there half the time. `currentColor`
 *   is whatever ink the host surface already reads against, which is why the
 *   icon itself is drawn in it. Keep the `focus-visible:` prefix - a bare
 *   `ring-current` would ring the control on mouse click too.
 * - `focus-visible:opacity-100` because `opacity` fades the ring along with the
 *   icon; at the resting 45% the indicator is too faint to satisfy a visible
 *   focus state.
 */
const REVEAL_TOGGLE_CLASS =
  'absolute top-0 right-0 h-full px-3 py-2 text-current opacity-45 hover:bg-transparent hover:text-current hover:opacity-70 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-current';

interface PasswordFieldProps<T extends FieldValues> {
  readonly control: Control<T>;
  readonly name: Path<T>;
  readonly label?: string;
  readonly placeholder?: string;
  readonly isDisabled?: boolean;
  /**
   * Surface overrides. The defaults dress the web auth card; the native entry
   * flow sits on a different material and passes its own. Optional so every
   * existing call site keeps the web look without touching it.
   */
  readonly inputClassName?: string;
  readonly labelClassName?: string;
}

export function PasswordField<T extends FieldValues>({
  control,
  name,
  label,
  placeholder,
  isDisabled = false,
  inputClassName = AUTH_PASSWORD_INPUT_CLASS,
  labelClassName = AUTH_LABEL_CLASS,
}: PasswordFieldProps<T>) {
  const { t } = useTranslation('auth');
  const [showPassword, setShowPassword] = useState(false);
  const resolvedLabel = label ?? t('login.passwordLabel');
  const resolvedPlaceholder = placeholder ?? t('login.passwordPlaceholder');

  return (
    <FormField control={control} name={name}>
      {({ field, fieldState }) => (
        <AuthTextField
          {...field}
          autoComplete="current-password"
          hint={fieldState.error?.message}
          inputClassName={inputClassName}
          isDisabled={isDisabled}
          isInvalid={fieldState.invalid}
          label={resolvedLabel}
          labelClassName={labelClassName}
          placeholder={resolvedPlaceholder}
          trailing={
            <Button
              aria-label={showPassword ? t('login.hidePassword') : t('login.showPassword')}
              className={REVEAL_TOGGLE_CLASS}
              color="tertiary"
              isDisabled={isDisabled}
              size="sm"
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
            >
              {showPassword ? (
                <EyeOff aria-hidden="true" className="size-5" />
              ) : (
                <Eye aria-hidden="true" className="size-5" />
              )}
            </Button>
          }
          type={showPassword ? 'text' : 'password'}
        />
      )}
    </FormField>
  );
}
